
import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
	ProSidebar,
	Menu,
	MenuItem,
	SidebarHeader,
	SidebarContent,
	SubMenu,
	SidebarFooter
} from "react-pro-sidebar";
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretUp } from '@fortawesome/free-solid-svg-icons';
import { FiLogOut, FiLock } from "react-icons/fi";
import { FiArrowLeftCircle, FiArrowRightCircle } from "react-icons/fi";
import "react-pro-sidebar/dist/css/styles.css";
import './Sidebar.css';
import { useSelector } from 'react-redux';

import { logoutRequest } from '../../modules/login/services/LoginActions';
import { SIDE_BAR_MENUS } from '../../constants/sidebarMenus';
import ChangePasswordModal from '../../modules/changePassword/modal/ChangePasswordModal';
import ProviderThemeSwitcher from './ProviderThemeSwitcher';
import ProviderThemeLogo from '../ProviderThemeLogo';
import { changePasswordRequest } from '../../modules/changePassword/services/changePasswordActions';
import {
	BASE_PATH_PREFIX,
	RAD_PROFILE,
	RAD_LIST,
	EDIT_RAD_PROFILE,
	SITE_LIST,
	SITE_PROFILE,
	EDIT_SITE_PROFILE,
	RAD_CUSTOM_VIEW,
	CONFIG_CUSTOM_VIEW,
	CONFIG_CUSTOM_VIEW_EDIT,
	CUSTOM_VIEW,
	WORKLIST_DEFINITION,
	WORKLIST_DEFINITION_CONFIGURATION,
	HOLIDAYDEMANDPLANNER,
	HOLIDAYPLANNER,
	LICENSE_PRIVILEGE_STATUS_MAPPING,
	LICENSE_MAPPING,
	PRIVILEGES_MAPPING,
	READING_STATUS_MAPPING,
	DEFINE_MISMATCH,
	TASK_CONFIGURATION,
	TASK_CONFIGURATION_PANEL,
	SITE_CUSTOM_VIEW,
	SITE_FACILITY_CUSTOM_VIEW,
	SITE_CONFIG_CUSTOM_VIEW,
	SITE_CONFIG_CUSTOM_VIEW_EDIT
} from '../../constants/routes';
import { assignedSitesCountRequest, unassignedSitesCountRequest } from "../../modules/common/services/commonActions";
import { ROLES } from '../../constants/roleConstants';
import { useSmartNavigation } from '../../hooks/useSmartNavigation';
import { applyHierarchyAliasesToMenus, filterSidebarMenusByHierarchyAccess, getHierarchyDisplayLabel, isRoleAllowed } from '../../utils/commonUtils';

const Sidebar = (props) => {
	const dispatch = useDispatch();
	const location = useLocation();
	const navigate = useNavigate();
	const smartNavigate = useSmartNavigation();
	const sidebarContentRef = useRef(null);
	const { assignedSitesCount, unassignedSitesCount } = useSelector(state => state.common);

	const { menuCollapse, menuIconClick, userData } = props;
	const [showDropdown, setShowDropdown] = useState(false);
	const [openSubMenu, setOpenSubMenu] = useState(null);
	const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);

	const handleLogout = () => {
		dispatch(logoutRequest());
		// since we use location, from state logic in Public/Private routes, need to reset the state here
		navigate(BASE_PATH_PREFIX, { replace: true });
	}

	// Mapping of child detail/edit pages to their parent list pages
	// This keeps the parent menu active when viewing detail or edit pages
	const childToParentRouteMapping = {
		[RAD_PROFILE]: RAD_LIST,
		[EDIT_RAD_PROFILE]: RAD_LIST,
		[SITE_PROFILE]: SITE_LIST,
		[EDIT_SITE_PROFILE]: SITE_LIST,
		[RAD_CUSTOM_VIEW]: CUSTOM_VIEW,
		[CONFIG_CUSTOM_VIEW]: CUSTOM_VIEW,
		[CONFIG_CUSTOM_VIEW_EDIT]: CUSTOM_VIEW,
		[WORKLIST_DEFINITION_CONFIGURATION]: WORKLIST_DEFINITION,
		[HOLIDAYDEMANDPLANNER]: HOLIDAYPLANNER,
		[LICENSE_MAPPING]: LICENSE_PRIVILEGE_STATUS_MAPPING,
		[PRIVILEGES_MAPPING]: LICENSE_PRIVILEGE_STATUS_MAPPING,
		[READING_STATUS_MAPPING]: LICENSE_PRIVILEGE_STATUS_MAPPING,
		[DEFINE_MISMATCH]: LICENSE_PRIVILEGE_STATUS_MAPPING,
		// covers all task panel tabs: /panel/:taskId/general|task-tags|provider-pool|compensation
		[TASK_CONFIGURATION_PANEL]: TASK_CONFIGURATION,
		[SITE_FACILITY_CUSTOM_VIEW]: SITE_CUSTOM_VIEW,
		[SITE_CONFIG_CUSTOM_VIEW]: SITE_CUSTOM_VIEW,
		[SITE_CONFIG_CUSTOM_VIEW_EDIT]: SITE_CUSTOM_VIEW,

	};

	// Helper function to check if current path matches a route (including with dynamic params)
	const pathMatchesRoute = (currentPath, routePattern) => {
		if (currentPath === routePattern) return true;
		// Check if path starts with route pattern followed by / (for dynamic params like /site/siteprofile/123)
		if (currentPath.startsWith(`${routePattern}/`)) return true;
		return false;
	};

	// Function to check if a menu item is active based on current pathname
	const isMenuActive = (menu) => {
		if (menu.link && location.pathname === menu.link) {
			return true;
		}
		// Check if current path is a child route that should activate this parent menu
		if (menu.link) {
			for (const [childRoute, parentRoute] of Object.entries(childToParentRouteMapping)) {
				if (menu.link === parentRoute && pathMatchesRoute(location.pathname, childRoute)) {
					return true;
				}
			}
		}
		// Check if any submenu is active
		if (menu.subMenus && menu.subMenus.length > 0) {
			return menu.subMenus.some(subMenu => isMenuActive(subMenu));
		}
		return false;
	};

	// Function to check if a submenu item is specifically active (exact match)
	const isSubMenuItemActive = (menu) => {
		if (!menu.link) return false;

		// Check exact match
		if (location.pathname === menu.link) {
			return true;
		}

		// Check if current path is a child route that should activate this parent menu
		for (const [childRoute, parentRoute] of Object.entries(childToParentRouteMapping)) {
			if (menu.link === parentRoute && pathMatchesRoute(location.pathname, childRoute)) {
				return true;
			}
		}

		return false;
	};

	// Handle click on parent menu when sidebar is collapsed
	const handleParentMenuClick = (menuIndex) => {
		if (menuCollapse) {
			// Expand the sidebar first
			menuIconClick();
			// Set which submenu should be opened after a short delay to allow sidebar expansion
			setTimeout(() => {
				setOpenSubMenu(menuIndex);
				// Keep the submenu open until manually closed
			}, 200);
		}
	};

	// Helper function to find the scrollable container
	const findScrollableContainer = () => {
		if (!sidebarContentRef.current) return null;

		// Try multiple approaches to find the scrollable container

		// 1. Look for the pro-sidebar-content class specifically
		let scrollContainer = sidebarContentRef.current.querySelector('.pro-sidebar-content');
		if (scrollContainer && scrollContainer.scrollHeight > scrollContainer.clientHeight) {
			return scrollContainer;
		}

		// 2. Check if the ref itself is scrollable
		if (sidebarContentRef.current.scrollHeight > sidebarContentRef.current.clientHeight) {
			return sidebarContentRef.current;
		}

		// 3. Look for any scrollable element within the sidebar
		const allElements = sidebarContentRef.current.querySelectorAll('*');
		for (let element of allElements) {
			const style = getComputedStyle(element);
			if ((style.overflow === 'auto' || style.overflow === 'scroll' ||
				style.overflowY === 'auto' || style.overflowY === 'scroll') &&
				element.scrollHeight > element.clientHeight) {
				return element;
			}
		}

		// 4. Fallback to the sidebar content ref
		return sidebarContentRef.current;
	};

	// Helper function to scroll to active menu (can be called programmatically)
	const scrollToActiveMenu = () => {
		if (!sidebarContentRef.current || menuCollapse) return;

		setTimeout(() => {
			const activeMenuItems = sidebarContentRef.current?.querySelectorAll('.active');
			if (activeMenuItems && activeMenuItems.length > 0) {
				const activeMenuItem = activeMenuItems[activeMenuItems.length - 1];
				const scrollContainer = findScrollableContainer();

				if (scrollContainer && activeMenuItem) {
					// Simple approach: just scroll the element into view
					activeMenuItem.scrollIntoView({
						behavior: 'smooth',
						block: 'center',
						inline: 'nearest'
					});
				}
			}
		}, 200);
	};

	// Reset openSubMenu when sidebar is collapsed
	useEffect(() => {
		if (menuCollapse) {
			setOpenSubMenu(null);
		} else {
			// When sidebar expands, scroll to active menu after a delay
			// setTimeout(() => {
			// }, 600); // Increased delay for sidebar expansion
			scrollToActiveMenu();
		}
	}, [menuCollapse]);

	useEffect(() => {
		dispatch(assignedSitesCountRequest());
		dispatch(unassignedSitesCountRequest());

		const interval = setInterval(() => {
			dispatch(assignedSitesCountRequest());
			dispatch(unassignedSitesCountRequest());
		}, 300000);
		return () => clearInterval(interval);
	}, [dispatch]);

	const getMenuLink = link => {
		if (link === RAD_PROFILE && isRoleAllowed([ROLES.PROVIDER], userData?.RoleCode)) {
			return `${link}?Id=${userData?.RadiologistID}`
		} else {
			return link
		}
	}

	const getSidebarMenuTitle = (title) => getHierarchyDisplayLabel(title, userData);

	// Recursive function to render menu items with unlimited nesting
	const renderMenuItems = (menuItems = []) => {
		const filteredMenuItems = menuItems.filter(menu => isRoleAllowed(menu?.roles, userData?.RoleCode));
		return filteredMenuItems.map((menu, index) => {
			const isActive = isMenuActive(menu);
			const isExactMatch = isSubMenuItemActive(menu);
			// shouldBeOpen should be true if:
			// 1. The menu or any of its submenus is active AND sidebar is not collapsed
			// 2. OR if this menu was manually opened via click when collapsed
			const shouldBeOpen = (isActive && !menuCollapse) || openSubMenu === index;

			// If the menu has subMenus, render as SubMenu with recursive children
			if (menu.subMenus && menu.subMenus.length > 0) {
				return (
					<SubMenu
						key={`submenu-${index}-${shouldBeOpen ? 'open' : 'closed'}`} // Force re-render with key change
						title={getSidebarMenuTitle(menu.title)}
						icon={menu.icon}
						className={`${(menuCollapse && isActive) ? 'active' : ''}`}
						defaultOpen={shouldBeOpen}
						onClick={() => handleParentMenuClick(index)}
					>
						{renderMenuItems(menu.subMenus)}
					</SubMenu>
				);
			}
			// Otherwise, render as a regular MenuItem
			const link = getMenuLink(menu.link);
			return (
				<MenuItem
					key={index}
					icon={menu.icon}
					className={isExactMatch ? 'active' : ''}
					onClick={(event) => {
						event?.preventDefault?.();
						if (menu.isExternal) {
							window.open(link, '_blank', 'noopener');
							return;
						}
						smartNavigate(link, { forceSameTab: true });
					}}
				>
					{getSidebarMenuTitle(menu.title)}
					{/* {menu.title === "Facility List" && (
						<span className="menu-count">{assignedSitesCount}</span>
					)} */}
					{menu.title === "Unassigned Facilities" && (
						<span className="unassigned-menu-count">{unassignedSitesCount}</span>
					)}
				</MenuItem>
			);
		});
	};

	const visibleMenus = applyHierarchyAliasesToMenus(
		filterSidebarMenusByHierarchyAccess(SIDE_BAR_MENUS, userData),
		userData,
	);

	return (
		<div className='sidebar-container'>
			<div className={`pro-sidebar ${menuCollapse ? "w-auto" : ""}`}>
				<div id="header">
					<ProSidebar collapsed={menuCollapse}>
						<SidebarHeader className='position-relative'>
							<div className={"logotext d-flex align-items-center justify-content-center h-100"}>
								{!menuCollapse && <ProviderThemeLogo className="cursor-pointer d-inline" src="/assets/logos/Encore-Logo.png" darkSrc="/assets/logos/Encore-Logo-Inverted.png" alt="Encore" style={{ width: 135 }} />}
								{menuCollapse && <img className="cursor-pointer" src="/assets/logos/EncoreLogoSort.png" alt="" style={{ width: 30, height: 30 }} />}
							</div>
							<div className="closemenu" onClick={menuIconClick}>
								{menuCollapse ? (
									<FiArrowRightCircle />
								) : (
									<FiArrowLeftCircle />
								)}
							</div>
						</SidebarHeader>
						<SidebarContent ref={sidebarContentRef}>
							<Menu>
								{renderMenuItems(visibleMenus)}
							</Menu>
						</SidebarContent>

						<SidebarFooter style={{ padding: "10px", borderTop: "1px solid #ddd" }}>
							<div
								className="sidebar-footer-user d-flex align-items-center justify-content-center cursor-pointer"
								style={{ height: "34px" }}
								onClick={() => {
									if (menuCollapse) {
										menuIconClick();
									} else {
										setShowDropdown(!showDropdown);
									}
								}}                            >
								<img
									src="/assets/logos/UserAvatar.png"
									alt="User"
									style={{
										width: 31,
										height: 32,
										borderRadius: "50%",
										marginRight: menuCollapse ? 0 : 10,
										marginLeft: menuCollapse ? 0 : "5px"
									}}
								/>
								{!menuCollapse && (
									<>
										<span className="user-name" style={{
											fontWeight: "400",
											flexGrow: 1,
											color: "#28abe7",
											fontSize: "15px",
											marginLeft: "8px",
											fontFamily: `system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", "Noto Sans", "Liberation Sans", Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"`
										}}>
											{userData?.FullName || ""}
										</span>
										<FontAwesomeIcon
											icon={faCaretUp}
											style={{
												fontWeight: 700,
												color: "#28abe7",
												cursor: "pointer",
												transform: showDropdown ? "rotate(-180deg)" : "rotate(0deg)",
												transition: "transform 0.1s ease"
											}}
										/>
									</>
								)}
							</div>

							{!menuCollapse && showDropdown && (
								<div
									className="sidebar-footer-dropdown mt-2 ps-2"
									style={{
										padding: "7px 0px",
										borderTop: "solid 1px #c7c7c7",
										// height: !menuCollapse ? "175px" : "0px"
									}}
								>
									<ProviderThemeSwitcher />
									<div
										className="d-flex align-items-center cursor-pointer"
										style={{ color: "#288db1", fontSize: "14px", fontWeight: "400", padding: "10px 5px" }}
										onClick={() => { setShowDropdown(false); setShowChangePasswordModal(true); }}
									>
										<FiLock style={{ fontSize: 16, marginRight: 5, color: "#2b6bb2", marginLeft: "4px", marginBottom: "3px" }} />
										<span style={{ cursor: 'pointer' }}>Change Password</span>
									</div>
									<div
										className="d-flex align-items-center cursor-pointer"
										style={{ color: "#288db1", fontSize: "14px", fontWeight: "400", padding: "7px 5px" }}
										onClick={props.logout}
									>
										<FiLogOut style={{ fontSize: 15, marginRight: 5, color: "#2b6bb2", marginLeft: "4px", marginBottom: "3px" }} />
										<span style={{ cursor: 'pointer' }} onClick={handleLogout}>Sign out</span>
									</div>
								</div>
							)}
						</SidebarFooter>
					</ProSidebar>
				</div>
			</div>

			<ChangePasswordModal
				show={showChangePasswordModal}
				onClose={() => setShowChangePasswordModal(false)}
				onSave={(payload) => dispatch(changePasswordRequest(payload))}
			/>
		</div>
	)
}

export default Sidebar;
